import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BookOpen, Sparkles, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Loader2, Share2, Copy, Eye, Zap, GraduationCap, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { shareStudyKit } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareViewPage = () => {
  const { shareId } = useParams();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [revealedQuizzes, setRevealedQuizzes] = useState({});
  const [expandedSections, setExpandedSections] = useState({ summary: true, flashcards: true, quizzes: true });

  useEffect(() => {
    const fetchKit = async () => {
      try {
        const res = await axios.get(`${API}/share/view/${shareId}`);
        setKit(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Study kit not found');
      } finally {
        setLoading(false);
      }
    };
    if (shareId) fetchKit();
  }, [shareId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleQuizSelect = (quizIdx, option) => {
    setQuizAnswers(prev => ({ ...prev, [quizIdx]: option }));
  };

  const revealQuiz = (quizIdx) => {
    setRevealedQuizzes(prev => ({ ...prev, [quizIdx]: true }));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center" data-testid="share-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-4" data-testid="share-error">
        <Card className="max-w-md w-full p-8 text-center  bg-white/5 backdrop-blur-xl border-white/10">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-bold text-white mb-2">Not Found</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/">
            <Button className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
              Go to iStudyPro
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const materials = kit?.materials || {};
  const flashcards = materials.flashcards || [];
  const quizzes = materials.quizzes || [];
  const summary = materials.summary || '';
  const keyPoints = materials.key_points || [];

  return (
    <div className="min-h-screen bg-[#0A0A0F]" data-testid="share-view-page">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-600/30 via-indigo-600/20 to-purple-600/30 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-white/80">iStudyPro</span>
            </div>
            <Button onClick={copyLink} variant="outline" size="sm" className="rounded-xl gap-1.5 border-white/20 text-white/70 hover:text-white hover:bg-white/10" data-testid="copy-share-link">
              <Copy className="w-3.5 h-3.5" /> Copy Link
            </Button>
            <Button onClick={() => shareStudyKit(shareId, kit?.title, (kit?.materials?.flashcards||[]).length, (kit?.materials?.quizzes||[]).length)}
              size="sm" className="rounded-xl gap-1.5 bg-green-600 hover:bg-green-700 text-white" data-testid="whatsapp-share-link">
              WhatsApp
            </Button>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1" data-testid="share-title">{kit.title}</h1>
          {kit.subject_name && (
            <span className="inline-block px-3 py-1 rounded-lg bg-purple-500/20 text-purple-300 text-xs font-medium mt-2">
              {kit.subject_name}
            </span>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-white/50">
            <span>Shared by {kit.shared_by}</span>
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {kit.views} views</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Summary Section */}
        {(summary || keyPoints.length > 0) && (
          <Card className=" bg-white/5 backdrop-blur-xl border-white/10 overflow-hidden" data-testid="share-summary-section">
            <button onClick={() => toggleSection('summary')} className="w-full flex items-center justify-between p-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" /> Summary & Key Points
              </h2>
              {expandedSections.summary ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            <AnimatePresence>
              {expandedSections.summary && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-5 pb-5 space-y-4">
                    {summary && <p className="text-sm text-white/70 leading-relaxed">{summary}</p>}
                    {keyPoints.length > 0 && (
                      <ul className="space-y-2">
                        {keyPoints.map((point, i) => (
                          <li key={`kp-${i}-${(point || '').slice(0, 16)}`} className="flex items-start gap-2 text-sm text-white/70">
                            <Zap className="w-3.5 h-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        )}

        {/* Flashcards Section */}
        {flashcards.length > 0 && (
          <Card className=" bg-white/5 backdrop-blur-xl border-white/10 overflow-hidden" data-testid="share-flashcards-section">
            <button onClick={() => toggleSection('flashcards')} className="w-full flex items-center justify-between p-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-cyan-400" /> Flashcards ({flashcards.length})
              </h2>
              {expandedSections.flashcards ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            <AnimatePresence>
              {expandedSections.flashcards && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-5 pb-5 space-y-3">
                    {flashcards.map((fc, i) => (
                      <FlashcardItem key={fc.id || `fc-${i}`} card={fc} index={i} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        )}

        {/* Quizzes Section */}
        {quizzes.length > 0 && (
          <Card className=" bg-white/5 backdrop-blur-xl border-white/10 overflow-hidden" data-testid="share-quizzes-section">
            <button onClick={() => toggleSection('quizzes')} className="w-full flex items-center justify-between p-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" /> Quizzes ({quizzes.length})
              </h2>
              {expandedSections.quizzes ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            <AnimatePresence>
              {expandedSections.quizzes && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-5 pb-5 space-y-4">
                    {quizzes.map((quiz, i) => (
                      <div key={quiz.id || `quiz-${i}`} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`share-quiz-${i}`}>
                        <p className="text-sm font-medium text-white mb-3">
                          <span className="text-green-400 mr-2">Q{i + 1}.</span>
                          {quiz.question}
                        </p>
                        <div className="space-y-2 mb-3">
                          {(quiz.options || []).map((opt, oi) => {
                            const isSelected = quizAnswers[i] === opt;
                            const isRevealed = revealedQuizzes[i];
                            const isCorrect = opt === quiz.correct;
                            return (
                              <button
                                key={`q${i}-opt-${oi}`}
                                onClick={() => !isRevealed && handleQuizSelect(i, opt)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-all ${
                                  isRevealed && isCorrect ? 'border-green-500/50 bg-green-500/15 text-green-300' :
                                  isRevealed && isSelected && !isCorrect ? 'border-red-500/50 bg-red-500/15 text-red-300' :
                                  isSelected ? 'border-purple-500/50 bg-purple-500/15 text-white' :
                                  'border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                                }`}
                                data-testid={`share-quiz-${i}-option-${oi}`}
                              >
                                <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                        {!revealedQuizzes[i] ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revealQuiz(i)}
                            disabled={!quizAnswers[i]}
                            className="rounded-lg gap-1.5 text-xs border-white/20 text-white/60 hover:text-white"
                            data-testid={`share-quiz-${i}-reveal`}
                          >
                            <Eye className="w-3 h-3" /> Check Answer
                          </Button>
                        ) : (
                          quiz.explanation && (
                            <p className="text-xs text-white/50 mt-2 p-2 rounded-lg bg-white/5">{quiz.explanation}</p>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        )}

        {/* CTA */}
        <Card className="p-6  bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/20 text-center" data-testid="share-cta">
          <GraduationCap className="w-10 h-10 mx-auto mb-3 text-purple-400" />
          <h3 className="text-lg font-bold text-white mb-1">Want to create your own study kits?</h3>
          <p className="text-sm text-white/60 mb-4">Join iStudyPro - AI-powered study assistant for students</p>
          <Link to="/login">
            <Button className="rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold" data-testid="share-signup-btn">
              Get Started <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
};

const FlashcardItem = ({ card, index }) => {
  const [flipped, setFlipped] = useState(false);
  const front = card.front || card.question || card.term || '';
  const back = card.back || card.answer || card.definition || '';

  return (
    <button
      onClick={() => setFlipped(!flipped)}
      className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.02] text-left transition-all hover:border-white/20"
      data-testid={`share-flashcard-${index}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-white/30 font-medium">
          {flipped ? 'Answer' : 'Question'} — tap to flip
        </span>
        <span className="text-[10px] text-white/30">#{index + 1}</span>
      </div>
      <p className="text-sm text-white/80">{flipped ? back : front}</p>
    </button>
  );
};

export default ShareViewPage;
